import Cookies from "js-cookie";
const GLOBAL_UTM_KEY = "UTM_URL";

export function handleSetCookie(name: string, value: any): void {
  Cookies.set(name, value, {
    expires: 7,
    path: "/",
    secure: process.env.NODE_ENV !== "development",
    sameSite: "strict",
  });
}

export function getCookie(name: string): string | undefined {
  const cookieValue = Cookies.get(name);
  return cookieValue;
}

export const removeCountryCode = (
  countryCode: string,
  mobileNumber: string
): string => {
  const countryCodeWithPlus = `+${countryCode}`;
  if (mobileNumber.startsWith(countryCodeWithPlus)) {
    return mobileNumber.slice(countryCodeWithPlus.length);
  }
  return mobileNumber;
};

export const getStoredUrlParams = (): string | null => {
  const cookieData = Cookies.get("UTM_URL") || localStorage.getItem("UTM_URL");
  if (!cookieData) return null;

  try {
    const parsed = JSON.parse(cookieData);
    if (Array.isArray(parsed) && parsed.length > 0) {
      const firstValid = parsed.find((entry) => entry.expiresAt > Date.now());
      return firstValid?.url || null;
    }
  } catch (error: any) {
    console.error("Invalid UTM data in storage", error);
  }

  return null;
};

export const getUtmParams = (storedParams?: string): Record<string, string> => {
  if (!storedParams) return {};

  const storedParamsObj = Object.fromEntries(new URLSearchParams(storedParams));


  const utmParams = {
    utm_id: storedParamsObj?.utm_id || "",
    utm_source: storedParamsObj?.utm_source || "",
    utm_medium: storedParamsObj?.utm_medium || "",
    utm_campaign: storedParamsObj?.utm_campaign || "",
    utm_term: storedParamsObj?.utm_term || "",
    utm_content: storedParamsObj?.utm_content || "",
  };


  return utmParams;
};

export const removeExpiredUtm = (): void => {
  const cookieUtms = Cookies.get(GLOBAL_UTM_KEY);
  const localStorageUtms = localStorage.getItem(GLOBAL_UTM_KEY);

  if (!cookieUtms && !localStorageUtms) return;

  let utmList: { url: string; expiresAt: number }[] = [];
  try {
    utmList = cookieUtms
      ? JSON.parse(cookieUtms)
      : localStorageUtms
      ? JSON.parse(localStorageUtms)
      : [];
  } catch (error) {
    console.error("Error parsing UTM data:", error);
    Cookies.remove(GLOBAL_UTM_KEY);
    localStorage.removeItem(GLOBAL_UTM_KEY);
    return;
  }

  const currentTime = Date.now();
  const validUtms = utmList.filter((entry) => entry.expiresAt > currentTime);

  if (validUtms.length === utmList.length) return;

  if (validUtms.length > 0) {
    Cookies.set(GLOBAL_UTM_KEY, JSON.stringify(validUtms), {
      expires: 30,
      path: "/",
    });
    localStorage.setItem(GLOBAL_UTM_KEY, JSON.stringify(validUtms));
  } else {
    Cookies.remove(GLOBAL_UTM_KEY);
    localStorage.removeItem(GLOBAL_UTM_KEY);
  }
};

/**
 * Gets the country selected by the user from cookies
 * @returns string | null - The selected country or null if not set
 */
export const getSelectedCountryByUser = (): string | null => {
  return getCookie('SelectedCountryByUser') || null;
};

/**
 * Sets the stayGlobal cookie when user chooses South Africa (Global)
 * This prevents automatic geolocation redirection
 */
export const setStayGlobal = (): void => {
  document.cookie = 'stayGlobal=true; path=/; max-age=31536000'; // 1 year
  // Clear SelectedCountryByUser when staying global
  document.cookie = 'SelectedCountryByUser=; path=/; max-age=0';
};

/**
 * Clears the stayGlobal cookie when user chooses a specific country
 * This allows automatic geolocation redirection to work again
 */
export const clearStayGlobal = (): void => {
  document.cookie = 'stayGlobal=; path=/; max-age=0';
};

/**
 * Clears SelectedCountryByUser cookie (used when switching to South Africa/Global)
 * This matches the Corporate Education project approach
 */
export const clearSelectedCountryByUser = (): void => {
  document.cookie = 'SelectedCountryByUser=; path=/; max-age=0';
};

/**
 * Sets the SelectedCountryByUser cookie with proper cleanup
 * @param country - The country name to set (e.g., 'nigeria', 'india')
 */
export const setSelectedCountryByUser = (country: string): void => {
  // Clear any existing cookie first
  document.cookie = 'SelectedCountryByUser=; path=/; max-age=0';
  
  // Set the new cookie
  document.cookie = `SelectedCountryByUser=${country}; path=/; max-age=31536000`; // 1 year
  
  // Clear stayGlobal when user selects a specific country
  document.cookie = 'stayGlobal=; path=/; max-age=0';
};
